import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useActualApi } from '../hooks/useActualApi';

interface UISettings {
  showRandomProfilePictures: boolean;
  showTriggerNotifications: boolean;
  // Add more UI settings here in the future
}

interface SettingsContextType {
  settings: UISettings;
  updateSetting: <K extends keyof UISettings>(key: K, value: UISettings[K]) => Promise<void>;
  resetSettings: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const defaultSettings: UISettings = {
  showRandomProfilePictures: true, // Default to showing random profile pictures
  showTriggerNotifications: true, // Default to showing trigger notifications
};

const STORAGE_KEY = 'dashboard_ui_settings';

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize with localStorage if available, otherwise defaults
  const getInitialSettings = (): UISettings => {
    try {
      const savedSettings = localStorage.getItem(STORAGE_KEY);
      if (savedSettings) {
        return { ...defaultSettings, ...JSON.parse(savedSettings) };
      }
    } catch (error) {
      console.error('Failed to load initial settings from localStorage:', error);
    }
    return defaultSettings;
  };

  const [settings, setSettings] = useState<UISettings>(getInitialSettings);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const { fetchClientUiSettings, updateClientUiSettings } = useActualApi();

  // Load settings from Firestore when user is authenticated
  const loadSettings = useCallback(async () => {
    if (!currentUser) {
      // If no user, load from localStorage as fallback
      try {
        const savedSettings = localStorage.getItem(STORAGE_KEY);
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          setSettings(prev => ({ ...prev, ...parsedSettings }));
        }
      } catch (error) {
        console.error('Failed to load settings from localStorage:', error);
      }
      setIsLoading(false); // Set loading to false when no user
      return;
    }

    setError(null);
    
    try {
      const response = await fetchClientUiSettings();
      if (response.success && response.settings) {
        setSettings(response.settings);
        // Also save to localStorage as backup
        localStorage.setItem(STORAGE_KEY, JSON.stringify(response.settings));
      }
    } catch (err) {
      console.error('Failed to load settings from Firestore:', err);
      setError('Failed to load settings');
      // Fallback to localStorage
      try {
        const savedSettings = localStorage.getItem(STORAGE_KEY);
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          setSettings(prev => ({ ...prev, ...parsedSettings }));
        }
      } catch (localError) {
        console.error('Failed to load settings from localStorage fallback:', localError);
      }
    } finally {
      setIsLoading(false); // Always set loading to false when done
    }
  }, [currentUser, fetchClientUiSettings]);

  // Load settings when component mounts or user changes
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Debug logging for settings
  useEffect(() => {
    console.log('⚙️ Settings Context - Current Settings:', {
      showRandomProfilePictures: settings.showRandomProfilePictures,
      showTriggerNotifications: settings.showTriggerNotifications,
      isLoading,
      currentUser: currentUser ? 'authenticated' : 'not authenticated'
    });
  }, [settings, isLoading, currentUser]);

  const updateSetting = async <K extends keyof UISettings>(key: K, value: UISettings[K]): Promise<void> => {
    console.log('⚙️ Settings Context - Updating setting:', { key, value });
    const newSettings = { ...settings, [key]: value };
    
    // Optimistically update local state
    setSettings(newSettings);
    
    // Save to localStorage immediately
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
    } catch (error) {
      console.error('Failed to save settings to localStorage:', error);
    }

    // If user is authenticated, save to Firestore
    if (currentUser) {
      setError(null);
      try {
        const payload = { [key]: value };
        const response = await updateClientUiSettings(payload);
        if (!response.success) {
          throw new Error(response.message || 'Failed to update settings');
        }
        // Update with server response to ensure consistency
        if (response.settings) {
          setSettings(response.settings);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(response.settings));
        }
      } catch (err) {
        console.error('Failed to update settings in Firestore:', err);
        setError('Failed to save settings');
        // Note: We don't revert the local state here to avoid poor UX
        // The settings will be re-synced on next load
      }
    }
  };

  const resetSettings = async (): Promise<void> => {
    setSettings(defaultSettings);
    
    // Clear localStorage
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear settings from localStorage:', error);
    }

    // If user is authenticated, reset in Firestore
    if (currentUser) {
      setError(null);
      try {
        const response = await updateClientUiSettings(defaultSettings);
        if (!response.success) {
          throw new Error(response.message || 'Failed to reset settings');
        }
      } catch (err) {
        console.error('Failed to reset settings in Firestore:', err);
        setError('Failed to reset settings');
      }
    }
  };

  return (
    <SettingsContext.Provider value={{ 
      settings, 
      updateSetting, 
      resetSettings, 
      isLoading, 
      error 
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};